import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18n } from '@/contexts/I18nContext';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ShipmentFilterSidebar, FilterValues } from '@/components/features/shipments/ShipmentFilterSidebar';
import { ShipmentCard } from '@/components/features/shipments/ShipmentCard';
import { ShipmentListItem } from '@/types/shipment.types';
import { ROUTES } from '@/lib/constants';
import { formatDate } from '@/lib/formatters';
import { PackageOpen, Plus, Eye, Filter, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const initialFilters: FilterValues = {
  search: '', status_code: '', city: '', service_type: '',
  driver_id: '', carrier_id: '', created_from: '', created_to: '',
};

export default function ShipmentsList() {
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const pagination = usePagination();
  const isMobile = useIsMobile();
  const { tp } = useI18n();
  
  const [shipments, setShipments] = useState<ShipmentListItem[]>([]);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  const debouncedSearch = useDebounce(filters.search, 300);

  useEffect(() => {
    fetchShipments();
  }, [pagination.page, debouncedSearch, filters.status_code, filters.city, filters.service_type, filters.driver_id, filters.carrier_id, filters.created_from, filters.created_to]);

  const fetchShipments = async () => {
    try {
      const params: Record<string, any> = { page: pagination.page, limit: pagination.limit };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status_code) params.status_code = filters.status_code;
      if (filters.city) params.city = filters.city;
      if (filters.service_type) params.service_type = filters.service_type;
      if (filters.driver_id) params.driver_id = filters.driver_id;
      if (filters.carrier_id) params.carrier_id = filters.carrier_id;
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;
      const response: any = await get('/api/v1/dashboard/shipments', params);
      setShipments(response.list);
      pagination.updatePagination({ total_pages: response.meta.totalPages, total: response.meta.total });
    } catch (error) { console.error('Failed to fetch shipments:', error); }
  };

  const handleShipmentClick = (shipment: ShipmentListItem) => navigate(ROUTES.SHIPMENT_DETAILS(shipment.id));
  const handleClearFilters = () => { setFilters(initialFilters); pagination.reset(); };
  const handleFilterChange = (key: keyof FilterValues, value: string) => setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));

  const columns = [
    { header: tp.shipments.trackingNumber, accessor: 'tracking_number' as keyof ShipmentListItem, className: 'font-medium' },
    { header: tp.shipments.status, accessor: (row: ShipmentListItem) => <StatusBadge status={row.status?.name_ || row.status?.code || ''} color={row.status?.color} /> },
    { header: tp.shipments.address, accessor: (row: ShipmentListItem) => row.address },
    { header: tp.shipments.recipient, accessor: (row: ShipmentListItem) => row.recipient_name },
    { header: tp.shipments.serviceType, accessor: (row: ShipmentListItem) => row.service_type },
    { header: tp.shipments.value, accessor: (row: ShipmentListItem) => `${row.total_amount}` },
    { header: tp.shipments.date, accessor: (row: ShipmentListItem) => formatDate(row.created_at), className: 'text-muted-foreground' },
  ];

  const renderActions = (row: ShipmentListItem) => (
    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleShipmentClick(row); }} title={tp.shipments.viewDetails}>
      <Eye className="h-4 w-4" />
    </Button>
  );

  const FilterSidebarContent = (
    <ShipmentFilterSidebar filters={filters} onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} />
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ChevronRight className="h-5 w-5" /></Button>
            <div><h1 className="text-xl md:text-2xl font-bold">{tp.shipments.pageTitle}</h1><p className="text-sm text-muted-foreground">{tp.shipments.pageSubtitle}</p></div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild><Button variant="outline" size="icon" className="lg:hidden"><Filter className="h-4 w-4" /></Button></SheetTrigger>
              <SheetContent side="right" className="w-72 p-4"><div className="mt-6">{FilterSidebarContent}</div></SheetContent>
            </Sheet>
            <Button onClick={() => navigate(ROUTES.CREATE_SHIPMENT)} className="gap-2 flex-1 sm:flex-none">
              <Plus className="h-4 w-4" />{tp.shipments.addNew}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">{FilterSidebarContent}</div>
        <div className="flex-1 min-w-0">
          {loading ? (
            <><div className="hidden md:block"><LoadingSkeleton type="table" rows={10} /></div><div className="md:hidden"><LoadingSkeleton type="card" rows={5} /></div></>
          ) : shipments.length === 0 ? (
            <EmptyState icon={<PackageOpen className="h-16 w-16" />} title={tp.shipments.emptyTitle} description={tp.shipments.emptyDescription}
              action={filters.search || filters.status_code ? { label: tp.shipments.clearFilters, onClick: handleClearFilters } : undefined}
            />
          ) : (
            <>
              <div className="hidden md:block">
                <DataTable data={shipments} columns={columns} selectable={true} selectedRows={selectedRows} onSelectionChange={setSelectedRows}
                  actions={renderActions} pagination={{ currentPage: pagination.page, totalPages: pagination.totalPages, total: pagination.total, onPageChange: pagination.goToPage }}
                  emptyMessage={tp.shipments.emptyTitle}
                />
              </div>
              <div className="md:hidden space-y-3">
                {shipments.map((shipment) => (
                  <ShipmentCard key={shipment.id} shipment={shipment} onClick={() => handleShipmentClick(shipment)} />
                ))}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-muted-foreground">
                      {tp.shipments.pagination.replace('{page}', String(pagination.page)).replace('{totalPages}', String(pagination.totalPages))}
                    </p>
                    <div className="flex gap-2">
                      <button onClick={() => pagination.prevPage()} disabled={!pagination.hasPrev} className="px-3 py-1 text-sm border rounded disabled:opacity-50">{tp.shipments.previous}</button>
                      <button onClick={() => pagination.nextPage()} disabled={!pagination.hasNext} className="px-3 py-1 text-sm border rounded disabled:opacity-50">{tp.shipments.next}</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
